import "./style.scss";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import { AOPMaltaInvoiceDEData, AOPMaltaInvoiceFRData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill } from "@/src/hooks/useAdmissionTaskPrefill";
import { syncCourseAndNationalityAutocompletes } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import {
  getInvoiceDoubleLangRegistry,
  type InvoiceDoubleLangCountry,
  type InvoiceSecondLangCode,
} from "../invoiceLetterDoubleLangRegistry";
import {
  pickAdmissionSecondLangFields,
  pickInvoiceSecondLangBody,
} from "../../AdmissionLetterDoubleLang/admissionLetterDoubleLangRegistry";

interface IAOPFormValues {
  whose_doc: string;
  coursePrefix: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  course: string;
  nationalitySecondLanguage: string;
  selectedCourseSecondLanguage: string;
  selectedCourse: string;
  mediumOfInstruction: string;
  mediumOfInstructionSecondLanguage: string;
  duration: string;
  durationSecondLanguage: string;
  invoiceBody: string;
  task_id?: string;
}

interface AOPConfigProps {
  country: InvoiceDoubleLangCountry;
  secondLang: InvoiceSecondLangCode;
  dataForGeneration: AOPMaltaInvoiceDEData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaInvoiceDEData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaInvoiceDEData>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ country, secondLang, dataForGeneration, setDataForGeneration, setBodyForPrev }) => {
  const registry = getInvoiceDoubleLangRegistry(country, secondLang);
  const secondLangInputClasses = {
    nationality: secondLang === "fr" ? "nationality-french" : "nationality-german",
    selectedCourse: secondLang === "fr" ? "selected-сourse-french" : "selected-сourse-german",
    medium: secondLang === "fr" ? "medium-of-instruction-french" : "medium-of-instruction-german",
    duration: secondLang === "fr" ? "duration-french" : "duration-german",
  };
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      invoiceBody: "",
      currentDate: formatDateForInput(today),
    },
  });

  const dispatch = useDispatch();
  const [invoiceContent, setInvoiceContent] = useState<string>("");
  const [invoiceContentSecondLanguage, setInvoiceContentSecondLanguage] = useState<string>("");
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const generatedDoc = useAdmissionGeneratedDocumentActions({ getValues });

  const course = watch("course");
  const invoiceBody = watch("invoiceBody");

  const offeredCourseAuto = useAutocomplete(Object.keys(registry.admissionTemplates));
  const invoiceBodyAuto = useAutocomplete(Object.keys(registry.invoiceTemplates));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "invoice",
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, offeredCourseAuto, nationalitiesAuto, whoseDocAuto);
    },
  });

  useEffect(() => {
    if (!course) return;
    const template = registry.admissionTemplates[course];
    if (!template) return;
    const secondValues = pickAdmissionSecondLangFields(secondLang, template);
    setValue("selectedCourse", template.selected_course);
    setValue("selectedCourseSecondLanguage", secondValues.course);
    setValue("mediumOfInstruction", template.medium_of_instruction);
    setValue("mediumOfInstructionSecondLanguage", secondValues.medium);
    setValue("duration", template.duration);
    setValue("durationSecondLanguage", secondValues.duration);
  }, [course, registry.admissionTemplates, secondLang, setValue]);

  useEffect(() => {
    if (!invoiceBody) {
      setInvoiceContent("");
      setInvoiceContentSecondLanguage("");
      return;
    }
    const template = registry.invoiceTemplates[invoiceBody];
    if (!template) {
      setInvoiceContent("");
      setInvoiceContentSecondLanguage("");
      return;
    }
    setInvoiceContent(template.tinyMCEContent);
    setInvoiceContentSecondLanguage(pickInvoiceSecondLangBody(secondLang, template));
  }, [invoiceBody, registry.invoiceTemplates, secondLang]);

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const response =
        secondLang === "fr"
          ? await admissionOfficeRequests.createInvoiceDocFrench(dataForGeneration as unknown as AOPMaltaInvoiceFRData)
          : await admissionOfficeRequests.createInvoiceDocGerman(dataForGeneration);
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const parseDate = (value: string) => {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    };

    const watchedFields = {
      currentDate: watch("currentDate"),
      fullName: watch("fullName"),
      dateOfBirth: watch("dateOfBirth"),
      nationality: watch("nationality"),
      selectedCourse: watch("selectedCourse"),
      whose_doc: watch("whose_doc"),
      duration: watch("duration"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      coursePrefix: watch("coursePrefix"),
      nationalitySecondLanguage: watch("nationalitySecondLanguage"),
      selectedCourseSecondLanguage: watch("selectedCourseSecondLanguage"),
      mediumOfInstructionSecondLanguage: watch("mediumOfInstructionSecondLanguage"),
      durationSecondLanguage: watch("durationSecondLanguage"),
    };

    setDataForGeneration((prev) => ({
      ...prev,
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      course: watchedFields.selectedCourse || "",
      whose_doc: watchedFields.whose_doc || "",
      duration: watchedFields.duration || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      coursePrefix: watchedFields.coursePrefix || "",
      invoiceBody: splitHtmlByBlocks(invoiceContent),
      invoiceBodySecondLanguage: splitHtmlByBlocks(invoiceContentSecondLanguage),
      nationalitySecondLanguage: watchedFields.nationalitySecondLanguage || "",
      courseSecondLanguage: watchedFields.selectedCourseSecondLanguage || "",
      mediumOfInstructionSecondLanguage: watchedFields.mediumOfInstructionSecondLanguage || "",
      durationSecondLanguage: watchedFields.durationSecondLanguage || "",
    }));

    setBodyForPrev((prev) => ({
      ...prev,
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      course: watchedFields.selectedCourse || "",
      whose_doc: watchedFields.whose_doc || "",
      duration: watchedFields.duration || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      coursePrefix: watchedFields.coursePrefix || "",
      invoiceBody: splitHtmlByBlocksWithFontConvert(invoiceContent),
      invoiceBodySecondLanguage: splitHtmlByBlocksWithFontConvert(invoiceContentSecondLanguage),
      nationalitySecondLanguage: watchedFields.nationalitySecondLanguage || "",
      courseSecondLanguage: watchedFields.selectedCourseSecondLanguage || "",
      mediumOfInstructionSecondLanguage: watchedFields.mediumOfInstructionSecondLanguage || "",
      durationSecondLanguage: watchedFields.durationSecondLanguage || "",
    }));

    if (invoiceContent.trim().length > 0 || invoiceContentSecondLanguage.trim().length > 0) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    }
  };

  const handleCopy = async (fieldName: keyof IAOPFormValues) => {
    const value = getValues(fieldName);
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  return (
    <div className="admission-office-page-config">
      <div className="admission-office-page-config-logo">
        <div className="logo-svg">
          <LogoSvg />
        </div>
        <div className="logo-line"></div>
        <p>Platforma</p>
      </div>
      <p className="admission-office-page-config-title">INVOICE DOUBLE LANGUAGE LETTER</p>
      <form className="admission-office-page-config-form">
        <div className="form-group-fields">
          <div className="form-group-fields-container">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.invoiceBody}
                required
                inputProps={{
                  register: register("whose_doc", { required: true }),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                }}
                identifier={"whose"}
              />
            </div>
            <div className="form-group-fields-input coursePrefix">
              <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
            </div>
          </div>
          <div className="form-group-fields-input currentDate">
            <div className="form-group-fields-input-label">
              <label htmlFor="currentDate">
                Date of Issue<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="currentDate" id="currentDate" type="date" {...register("currentDate", { required: true })} />
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="full-mame">
                Full Name<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
            <div className="form-group-fields-input-svg">
              <div className="form-group-fields-input-svg-line"></div>
              <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                <AODSvgExport.FullNameCopySvg />
              </div>
            </div>
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="dateOfBirth">
                Data of Birth<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth", { required: true })} />
          </div>
          <AutocompleteInput
            label="Nationality"
            value={nationalitiesAuto.query}
            onChange={nationalitiesAuto.onChange}
            onSelect={(v) => {
              nationalitiesAuto.onSelect(v);
              setValue("nationality", v);
            }}
            isOpen={nationalitiesAuto.isOpen}
            options={nationalitiesAuto.filtered}
            error={!!errors.nationality}
            required
            inputProps={{
              register: register("nationality", { required: true }),
              close: nationalitiesAuto.close,
              setOpen: nationalitiesAuto.onClick,
            }}
            identifier={"nationality"}
          />
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input course">
            <AutocompleteInput
              label="Select the offered course"
              value={offeredCourseAuto.query}
              onChange={offeredCourseAuto.onChange}
              onSelect={(v) => {
                offeredCourseAuto.onSelect(v);
                setValue("course", v);
              }}
              isOpen={offeredCourseAuto.isOpen}
              options={offeredCourseAuto.filtered}
              error={!!errors.course}
              required
              inputProps={{
                register: register("course", { required: true }),
                close: offeredCourseAuto.close,
                setOpen: offeredCourseAuto.onClick,
                setValue,
                clearErrors,
                name: "course",
              }}
              identifier={"offered-course-auto"}
            />
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="nationalitySecondLanguage">
                Nationality ({secondLang.toUpperCase()})<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className={secondLangInputClasses.nationality}
              id="nationalitySecondLanguage"
              type="text"
              {...register("nationalitySecondLanguage", { required: true })}
            />
          </div>
        </div>
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="selectedCourseSecondLanguage">
                Selected course ({secondLang.toUpperCase()})<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className={secondLangInputClasses.selectedCourse}
              id="selectedCourseSecondLanguage"
              type="text"
              {...register("selectedCourseSecondLanguage", { required: true })}
            />
          </div>
        </div>
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="selectedCourse">
                Selected course<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="selected-сourse" id="selectedCourse" type="text" {...register("selectedCourse", { required: true })} />
          </div>
        </div>
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="mediumOfInstructionSecondLanguage">
                Medium ({secondLang.toUpperCase()})<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className={secondLangInputClasses.medium}
              id="mediumOfInstructionSecondLanguage"
              type="text"
              {...register("mediumOfInstructionSecondLanguage", { required: true })}
            />
          </div>
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="durationSecondLanguage">
                Duration ({secondLang.toUpperCase()})<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className={secondLangInputClasses.duration}
              id="durationSecondLanguage"
              type="text"
              {...register("durationSecondLanguage", { required: true })}
            />
          </div>
        </div>
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="mediumOfInstruction">
                Medium of Instruction<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className="medium-of-instruction"
              id="mediumOfInstruction"
              type="text"
              {...register("mediumOfInstruction", { required: true })}
            />
          </div>
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="duration">
                Duration<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="duration" id="duration" type="text" {...register("duration", { required: true })} />
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input invoiceBody">
            <AutocompleteInput
              label="Invoice details"
              value={invoiceBodyAuto.query}
              onChange={invoiceBodyAuto.onChange}
              onSelect={(v) => {
                invoiceBodyAuto.onSelect(v);
                setValue("invoiceBody", v);
              }}
              isOpen={invoiceBodyAuto.isOpen}
              options={invoiceBodyAuto.filtered}
              error={!!errors.invoiceBody}
              required
              inputProps={{
                register: register("invoiceBody", { required: true }),
                close: invoiceBodyAuto.close,
                setOpen: invoiceBodyAuto.onClick,
              }}
              identifier={"invoice"}
            />
          </div>
        </div>

        <label>Invoice body second language</label>
        <Editor
          value={invoiceContentSecondLanguage}
          init={{
            height: 300,
            menubar: false,
            plugins: ["lists", "link", "code", "table"],
            toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
            branding: false,
            toolbar_mode: "wrap",
            licenseKey: "gpl",
            license_key: "gpl" as any,
          }}
          onEditorChange={setInvoiceContentSecondLanguage}
          tinymceScriptSrc="/tinymce/tinymce.min.js"
        />
        <label>Invoice body</label>
        <Editor
          value={invoiceContent}
          init={{
            height: 300,
            menubar: false,
            plugins: ["lists", "link", "code", "table"],
            toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
            branding: false,
            toolbar_mode: "wrap",
            licenseKey: "gpl",
            license_key: "gpl" as any,
          }}
          onEditorChange={setInvoiceContent}
          tinymceScriptSrc="/tinymce/tinymce.min.js"
        />

        <AOPGeneratedDocumentToolbar
          isGenerated={generatedDoc.isGenerated}
          generatedPdfUrl={generatedDoc.generatedPdfUrl}
          isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
          setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
          generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
          onDownload={generatedDoc.handleDownloadGeneratedPdf}
          onApprove={generatedDoc.handleApproveGeneratedDocument}
          onReject={generatedDoc.handleRejectGeneratedDocument}
          previewButton={
            <BtnDefault onClick={handlePreview} reverse={true}>
              <p>Preview doc</p>
              <AODSvgExport.PreviewSvg />
            </BtnDefault>
          }
          generateButton={
            <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
              <p>Generate doc</p>
              <AODSvgExport.GenerateSvg />
            </BtnDefault>
          }
        />
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="task_id">Task Id</label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="task_id" id="task_id" type="text" {...register("task_id")} />
            <div className="form-group-fields-input-svg">
              <div className="form-group-fields-input-svg-line"></div>
              <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("task_id")}>
                <AODSvgExport.FullNameCopySvg />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AOPConfig;
